import type { ComicCard } from '../sources/types.ts';
import { getDb, rowKey, type FavoriteRow } from './schema.ts';

export type FavoriteSaveKind = 'app' | 'source';

function activeSince(savedAt: number | undefined, unsavedAt: number | undefined): number | undefined {
  if (savedAt === undefined) return undefined;
  return (unsavedAt ?? 0) < savedAt ? savedAt : undefined;
}

function latestTimestamp(row: FavoriteRow): number {
  return Math.max(
    row.addedAt,
    row.appSavedAt ?? 0,
    row.appUnsavedAt ?? 0,
    row.sourceSavedAt ?? 0,
    row.sourceUnsavedAt ?? 0,
    row.sourceLastSyncedAt ?? 0,
  );
}

export function normalizeFavoriteRow(row: FavoriteRow): FavoriteRow {
  if (
    row.appSavedAt !== undefined ||
    row.appUnsavedAt !== undefined ||
    row.sourceSavedAt !== undefined ||
    row.sourceUnsavedAt !== undefined
  ) {
    return row;
  }
  return { ...row, appSavedAt: row.addedAt };
}

export function isAppSaved(row: FavoriteRow): boolean {
  const normalized = normalizeFavoriteRow(row);
  return activeSince(normalized.appSavedAt, normalized.appUnsavedAt) !== undefined;
}

export function isSourceSaved(row: FavoriteRow): boolean {
  const normalized = normalizeFavoriteRow(row);
  return activeSince(normalized.sourceSavedAt, normalized.sourceUnsavedAt) !== undefined;
}

export function isActiveFavorite(row: FavoriteRow): boolean {
  return isAppSaved(row) || isSourceSaved(row);
}

export function favoriteSortTimestamp(row: FavoriteRow): number {
  const normalized = normalizeFavoriteRow(row);
  return Math.max(
    activeSince(normalized.appSavedAt, normalized.appUnsavedAt) ?? 0,
    activeSince(normalized.sourceSavedAt, normalized.sourceUnsavedAt) ?? 0,
    0,
  );
}

export function mergeFavoriteRows(existing: FavoriteRow, incoming: FavoriteRow): FavoriteRow {
  const local = normalizeFavoriteRow(existing);
  const remote = normalizeFavoriteRow(incoming);
  const localDisplayAt = favoriteSortTimestamp(local);
  const remoteDisplayAt = favoriteSortTimestamp(remote);
  const display = remoteDisplayAt >= localDisplayAt ? remote : local;
  const merged: FavoriteRow = {
    ...display,
    key: local.key,
    source: local.source,
    comicId: local.comicId,
    appSavedAt: Math.max(local.appSavedAt ?? 0, remote.appSavedAt ?? 0) || undefined,
    appUnsavedAt: Math.max(local.appUnsavedAt ?? 0, remote.appUnsavedAt ?? 0) || undefined,
    sourceSavedAt: Math.max(local.sourceSavedAt ?? 0, remote.sourceSavedAt ?? 0) || undefined,
    sourceUnsavedAt: Math.max(local.sourceUnsavedAt ?? 0, remote.sourceUnsavedAt ?? 0) || undefined,
    sourceLastSyncedAt:
      Math.max(local.sourceLastSyncedAt ?? 0, remote.sourceLastSyncedAt ?? 0) || undefined,
  };
  const sourceSavedAt = merged.sourceSavedAt ?? 0;
  if ((remote.sourceSavedAt ?? 0) >= sourceSavedAt && remote.sourceNativeCat !== undefined) {
    merged.sourceNativeCat = remote.sourceNativeCat;
  } else if ((local.sourceSavedAt ?? 0) >= sourceSavedAt && local.sourceNativeCat !== undefined) {
    merged.sourceNativeCat = local.sourceNativeCat;
  }
  if (!isSourceSaved(merged)) delete merged.sourceNativeCat;
  merged.addedAt = Math.max(favoriteSortTimestamp(merged), latestTimestamp(merged));
  return merged;
}

function rowFromCard(card: ComicCard, savedAt: number): FavoriteRow {
  return {
    key: rowKey(card.source, card.id),
    source: card.source,
    comicId: card.id,
    addedAt: savedAt,
    titleSnapshot: card.title,
    coverUrl: card.coverUrl,
  };
}

function shouldKeepRow(row: FavoriteRow): boolean {
  return isActiveFavorite(row) || row.appUnsavedAt !== undefined || row.sourceUnsavedAt !== undefined;
}

export async function listFavoritesBySource(source: string): Promise<FavoriteRow[]> {
  const db = await getDb();
  return (await db.getAllFromIndex('favorites', 'by-source', source)).map(normalizeFavoriteRow);
}

export async function listAllFavorites(): Promise<FavoriteRow[]> {
  const db = await getDb();
  return (await db.getAll('favorites')).map(normalizeFavoriteRow);
}

export async function getFavorite(source: string, comicId: string): Promise<FavoriteRow | undefined> {
  const db = await getDb();
  const row = await db.get('favorites', rowKey(source, comicId));
  return row ? normalizeFavoriteRow(row) : undefined;
}

export async function updateFavoriteAddedAt(
  source: string,
  comicId: string,
  addedAt: number,
): Promise<boolean> {
  const db = await getDb();
  const existing = await db.get('favorites', rowKey(source, comicId));
  if (!existing) return false;
  await db.put('favorites', { ...normalizeFavoriteRow(existing), addedAt });
  return true;
}

export async function addFavorite(
  card: ComicCard,
  opts: { addedAt?: number } = {},
): Promise<void> {
  const db = await getDb();
  const savedAt = opts.addedAt ?? Date.now();
  const existing = await db.get('favorites', rowKey(card.source, card.id));
  const base = existing ? normalizeFavoriteRow(existing) : rowFromCard(card, savedAt);
  const row: FavoriteRow = {
    ...base,
    titleSnapshot: card.title,
    coverUrl: card.coverUrl,
    appSavedAt: savedAt,
    appUnsavedAt: undefined,
  };
  row.addedAt = favoriteSortTimestamp(row) || savedAt;
  await db.put('favorites', row);
}

export async function removeFavorite(source: string, comicId: string): Promise<void> {
  const db = await getDb();
  const key = rowKey(source, comicId);
  const existing = await db.get('favorites', key);
  if (!existing) return;
  const unsavedAt = Date.now();
  const row: FavoriteRow = {
    ...normalizeFavoriteRow(existing),
    appSavedAt: undefined,
    appUnsavedAt: unsavedAt,
  };
  row.addedAt = favoriteSortTimestamp(row) || unsavedAt;
  if (shouldKeepRow(row)) await db.put('favorites', row);
  else await db.delete('favorites', key);
}

export async function isFavorited(source: string, comicId: string): Promise<boolean> {
  const row = await getFavorite(source, comicId);
  return row !== undefined && isActiveFavorite(row);
}

export async function markSourceFavorite(
  card: ComicCard,
  opts: { sourceNativeCat?: number; savedAt?: number; syncedAt?: number } = {},
): Promise<void> {
  const db = await getDb();
  const savedAt = opts.savedAt ?? Date.now();
  const syncedAt = opts.syncedAt ?? savedAt;
  const existing = await db.get('favorites', rowKey(card.source, card.id));
  const base = existing ? normalizeFavoriteRow(existing) : rowFromCard(card, savedAt);
  const row: FavoriteRow = {
    ...base,
    titleSnapshot: card.title,
    coverUrl: card.coverUrl,
    sourceSavedAt: savedAt,
    sourceUnsavedAt: undefined,
    sourceLastSyncedAt: syncedAt,
  };
  if (opts.sourceNativeCat !== undefined) row.sourceNativeCat = opts.sourceNativeCat;
  row.addedAt = favoriteSortTimestamp(row) || savedAt;
  await db.put('favorites', row);
}

export async function unmarkSourceFavorite(source: string, comicId: string): Promise<void> {
  const db = await getDb();
  const key = rowKey(source, comicId);
  const existing = await db.get('favorites', key);
  if (!existing) return;
  const unsavedAt = Date.now();
  const row: FavoriteRow = {
    ...normalizeFavoriteRow(existing),
    sourceSavedAt: undefined,
    sourceUnsavedAt: unsavedAt,
    sourceLastSyncedAt: unsavedAt,
  };
  delete row.sourceNativeCat;
  row.addedAt = favoriteSortTimestamp(row) || unsavedAt;
  if (shouldKeepRow(row)) await db.put('favorites', row);
  else await db.delete('favorites', key);
}
